import { Fragment, useState, useEffect, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Webhook, Plus, Trash2, KeyRound, AlertTriangle, Code, List, X, Eye, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react'
import { Switch } from '../components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { toast } from '../lib/toast.js'
import postCreateAction from '../functions/API/postCreateAction.js'
import postUpdateAction from '../functions/API/postUpdateAction.js'
import postRotateActionSecret from '../functions/API/postRotateActionSecret.js'
import postPreviewAction from '../functions/API/postPreviewAction.js'
import JsonCodeEditor from '../components/JsonCodeEditor.js'

const inputClass =
  'block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600';
const labelClass = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

// Set a possibly-dotted key ("customer.ref") into a nested object.
function setNested(obj, dottedKey, value) {
  const parts = dottedKey.split('.').map((p) => p.trim()).filter(Boolean);
  if (!parts.length) return;
  let node = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (typeof node[parts[i]] !== 'object' || node[parts[i]] === null) node[parts[i]] = {};
    node = node[parts[i]];
  }
  node[parts[parts.length - 1]] = value;
}

// Compile the "Simple" rows into a template string. A row whose token is the
// __static__ sentinel emits its literal text; otherwise it emits a substitution
// token the server resolves.
function compileVisual(rows) {
  const obj = {};
  rows.filter((r) => r.key.trim()).forEach((r) => {
    setNested(obj, r.key, r.token === '__static__' ? r.staticText : `{{${r.token}}}`);
  });
  return JSON.stringify(obj, null, 2);
}

// Human-readable preview of what a single row will send.
function sampleLabel(row, sampleByKey) {
  if (row.token === '__static__') return row.staticText ? JSON.stringify(row.staticText) : '(empty)';
  const s = sampleByKey[row.token];
  return s === undefined ? '' : JSON.stringify(s);
}

// Every {{ token }} referenced by a template.
function extractTokens(template) {
  const out = [];
  const re = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;
  let m;
  while ((m = re.exec(template)) !== null) out.push(m[1]);
  return [...new Set(out)];
}

// Group captured fields AND step results together by the step they belong to,
// ordered by step number — so everything from "Step 2" appears in one place.
function buildStepGroups(fields, results) {
  const groups = [];
  const index = {};
  const add = (it) => {
    const id = it.step_id;
    if (index[id] === undefined) {
      index[id] = groups.length;
      groups.push({ stepId: id, stepNumber: it.step_number, stepName: it.step_name, items: [] });
    }
    groups[index[id]].items.push(it);
  };
  fields.forEach(add);
  results.forEach(add);
  groups.sort((a, b) => (a.stepNumber || 0) - (b.stepNumber || 0));
  return groups;
}

// "Step 2: Personal information" — makes it clear which step a field came from.
function stepGroupLabel(g) {
  return g.stepNumber ? `Step ${g.stepNumber}: ${g.stepName}` : g.stepName;
}

// Group the metadata variables by their server-provided `group` (Event, Step,
// Customer, …), preserving catalog order.
function groupVariables(variables) {
  const groups = [];
  const index = {};
  variables.forEach((v) => {
    const g = v.group || 'Details';
    if (index[g] === undefined) {
      index[g] = groups.length;
      groups.push({ group: g, items: [] });
    }
    groups[index[g]].items.push(v);
  });
  return groups;
}

export default function ActionEditorModal({
  open,
  setOpen,
  taskFlowId,
  step,
  action,           // null = create
  variables = [],   // event/flow metadata [{key,label,description}]
  fields = [],      // captured fields [{key,label,field_type,step_id,step_name}]
  results = [],     // step results [{key,label,step_type}]
  defaultTemplate = '',
  onSaved,
  onShowSecret,
}) {
  const isEdit = !!action;
  const editorApiRef = useRef(null);

  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState([]);
  const [authType, setAuthType] = useState('none');
  const [authHeaderName, setAuthHeaderName] = useState('');
  const [authCredential, setAuthCredential] = useState('');
  const [signingEnabled, setSigningEnabled] = useState(true);
  const [timeoutSeconds, setTimeoutSeconds] = useState(10);
  const [maxAttempts, setMaxAttempts] = useState(5);

  const [payloadMode, setPayloadMode] = useState('default'); // default | custom
  const [builderTab, setBuilderTab] = useState('simple');    // simple | advanced
  const [visualRows, setVisualRows] = useState([]);
  const [template, setTemplate] = useState('');

  const [preview, setPreview] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [paletteSearch, setPaletteSearch] = useState('');
  const [openSections, setOpenSections] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const paletteRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const toggleSection = (key) => setOpenSections((s) => ({ ...s, [key]: !s[key] }));
  const insertVariable = (key) => { insertToken(key); setPaletteOpen(false); };

  const allowedKeys = [
    ...variables.map((v) => v.key),
    ...fields.map((f) => f.key),
    ...results.map((r) => r.key),
  ];
  const stepGroups = buildStepGroups(fields, results);
  const metaGroups = groupVariables(variables);
  const headerCount = headers.filter((h) => h.name.trim()).length;
  const authLabel = authType === 'bearer' ? 'Bearer token' : authType === 'header' ? 'Custom header' : 'None';
  const sampleByKey = {};
  [...variables, ...fields, ...results].forEach((it) => { sampleByKey[it.key] = it.sample; });
  const defaultToken = fields[0]?.key || variables[0]?.key || '__static__';

  useEffect(() => {
    if (!open) return;
    if (action) {
      setName(action.name || '');
      setIsActive(action.is_active ?? true);
      setUrl(action.url || '');
      setHeaders((action.custom_headers || []).map((h) => ({ name: h.name, value: h.value })));
      setAuthType(action.auth_type || 'none');
      setAuthHeaderName('');
      setAuthCredential('');
      setSigningEnabled(action.signing_enabled ?? true);
      setTimeoutSeconds(action.timeout_seconds ?? 10);
      setMaxAttempts(action.max_attempts ?? 5);
      if (action.payload_template) {
        setPayloadMode('custom');
        setBuilderTab('advanced');
        setTemplate(action.payload_template);
        setVisualRows([]);
      } else {
        setPayloadMode('default');
        setBuilderTab('simple');
        setTemplate(defaultTemplate || '');
        setVisualRows([]);
      }
    } else {
      setName('');
      setIsActive(true);
      setUrl('');
      setHeaders([]);
      setAuthType('none');
      setAuthHeaderName('');
      setAuthCredential('');
      setSigningEnabled(true);
      setTimeoutSeconds(10);
      setMaxAttempts(5);
      setPayloadMode('default');
      setBuilderTab('simple');
      setVisualRows([]);
      setTemplate(defaultTemplate || '');
    }
    setPreview('');
    setPreviewError('');
    setPaletteSearch('');
    setOpenSections({});
    setShowPreview(false);
    setPaletteOpen(false);
  }, [open, action, defaultTemplate]);

  const addHeader = () => setHeaders([...headers, { name: '', value: '' }]);
  const updateHeader = (i, field, val) =>
    setHeaders(headers.map((h, idx) => (idx === i ? { ...h, [field]: val } : h)));
  const removeHeader = (i) => setHeaders(headers.filter((_, idx) => idx !== i));

  const addRow = () => setVisualRows([...visualRows, { key: '', token: defaultToken, staticText: '' }]);
  const updateRow = (i, field, val) =>
    setVisualRows(visualRows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeRow = (i) => setVisualRows(visualRows.filter((_, idx) => idx !== i));

  const switchToAdvanced = () => {
    if (builderTab === 'simple' && visualRows.some((r) => r.key.trim())) {
      setTemplate(compileVisual(visualRows));
    }
    setBuilderTab('advanced');
  }

  const insertToken = (key) => {
    editorApiRef.current?.insertText(`{{${key}}}`);
  }

  // The final template string to submit (null = server default payload).
  const resolveTemplate = () => {
    if (payloadMode === 'default') return null;
    return builderTab === 'simple' ? compileVisual(visualRows) : template;
  }

  // ── Advanced (JSON) editor helpers ──────────────────────────────────
  const matchSearch = (items) => {
    const q = paletteSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (it.label || '').toLowerCase().includes(q) || it.key.toLowerCase().includes(q));
  };

  const usedTokens = extractTokens(template);
  let jsonValid = true;
  try { if (template.trim()) JSON.parse(template); } catch (e) { jsonValid = false; }
  const advUnknown = usedTokens.filter((t) => !allowedKeys.includes(t));

  const formatTemplate = () => {
    try { setTemplate(JSON.stringify(JSON.parse(template), null, 2)); }
    catch (e) { toast.error("Can't format — fix the JSON first."); }
  }
  const resetTemplate = () => setTemplate(defaultTemplate || '');

  // Close the insert-variable dropdown when clicking outside it.
  useEffect(() => {
    if (!paletteOpen) return;
    const onDown = (e) => {
      if (paletteRef.current && !paletteRef.current.contains(e.target)) setPaletteOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [paletteOpen]);

  // Live preview: only fetched while the preview panel is open.
  useEffect(() => {
    if (!open || !showPreview) return;
    const tpl = payloadMode === 'default' ? null : (builderTab === 'simple' ? compileVisual(visualRows) : template);
    const handle = setTimeout(async () => {
      try {
        const res = await postPreviewAction({
          task_flow_id: taskFlowId,
          task_flow_step_id: step?.id,
          payload_template: tpl,
        });
        if (res.response.ok) { setPreview(res.data.preview || ''); setPreviewError(''); }
        else { setPreview(''); setPreviewError(res.data?.message?.payload_template?.[0] || 'Payload is not valid.'); }
      } catch (e) {
        setPreview(''); setPreviewError('Preview unavailable.');
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [open, showPreview, payloadMode, builderTab, template, visualRows, taskFlowId, step?.id]);

  const validate = () => {
    if (!name.trim()) { toast.error('Give the action a name.'); return false; }
    if (!/^https:\/\//i.test(url.trim())) { toast.error('The destination URL must use HTTPS.'); return false; }
    for (const h of headers) {
      if (h.name && !/^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/.test(h.name)) {
        toast.error(`Invalid header name: ${h.name}`); return false;
      }
    }
    if (authType === 'header' && authCredential && !authHeaderName.trim()) {
      toast.error('Enter a header name for header authentication.'); return false;
    }
    const tpl = resolveTemplate();
    if (tpl !== null) {
      try { JSON.parse(tpl); }
      catch (e) { toast.error('Payload is not valid JSON.'); return false; }
      const unknown = extractTokens(tpl).filter((t) => !allowedKeys.includes(t));
      if (unknown.length) { toast.error(`Unknown variables: ${unknown.join(', ')}`); return false; }
    }
    return true;
  }

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const body = {
      name: name.trim(),
      is_active: isActive,
      url: url.trim(),
      payload_template: resolveTemplate(),
      custom_headers: headers.filter((h) => h.name.trim()).map((h) => ({ name: h.name.trim(), value: h.value })),
      auth_type: authType,
      signing_enabled: signingEnabled,
      timeout_seconds: Number(timeoutSeconds) || 10,
      max_attempts: Number(maxAttempts) || 5,
    };
    if (isEdit) body.id = action.id;
    else { body.task_flow_id = taskFlowId; body.task_flow_step_id = step.id; }
    if (authType !== 'none' && authCredential) {
      body.auth_credential = authCredential;
      if (authType === 'header') body.auth_header_name = authHeaderName.trim();
    }
    try {
      const res = isEdit ? await postUpdateAction(body) : await postCreateAction(body);
      if (res.response.ok) {
        if (res.data?.signing_secret) onShowSecret?.(res.data.signing_secret);
        toast.success(isEdit ? 'Action updated.' : 'Action created.');
        onSaved?.();
        setOpen(false);
      } else {
        toast.error(res.data?.message?.payload_template?.[0] || res.data?.message?.url?.[0] || res.data?.error || 'Could not save the action.');
      }
    } catch (e) {
      toast.error('Could not save the action.');
    } finally {
      setSaving(false);
    }
  }

  const handleRotate = async () => {
    if (!isEdit) return;
    try {
      const res = await postRotateActionSecret(action.id);
      if (res.response.ok && res.data?.signing_secret) {
        onShowSecret?.(res.data.signing_secret);
        toast.success('Signing secret rotated.');
      } else {
        toast.error('Could not rotate the secret.');
      }
    } catch (e) {
      toast.error('Could not rotate the secret.');
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => !saving && setOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-100" enterFrom="opacity-0" enterTo="opacity-90"
          leave="ease-in duration-100" leaveFrom="opacity-90" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-100" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-100" leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-zinc-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 dark:border-zinc-700">
                  <div className="flex items-center gap-2">
                    <Webhook className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <Dialog.Title as="h3" className="text-base font-semibold text-gray-900 dark:text-white">
                      {isEdit ? 'Edit action' : 'Add action'}
                    </Dialog.Title>
                  </div>
                  <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-700" onClick={() => !saving && setOpen(false)}>
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                </div>

                <div className="px-5 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
                  {/* Basics */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <FieldLabel>Name</FieldLabel>
                      <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Notify CRM" />
                    </div>
                    <div className="flex items-end pb-1.5">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch checked={isActive} onCheckedChange={setIsActive} />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                      </label>
                    </div>
                  </div>

                  {/* Destination */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div className="sm:col-span-3">
                      <FieldLabel tip="The HTTPS web address the request is sent to. This is provided by the system you're connecting to.">Destination URL</FieldLabel>
                      <input className={inputClass} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/webhook" />
                    </div>
                    <div>
                      <FieldLabel>Method</FieldLabel>
                      <input className={`${inputClass} opacity-60`} value="POST" disabled readOnly />
                    </div>
                  </div>

                  {/* Payload */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <SectionHeading tip="The information sent to the receiving system when the step completes. Leave Customise off to send a standard set of details, or turn it on to choose exactly what's included.">Payload</SectionHeading>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch checked={payloadMode === 'custom'} onCheckedChange={(v) => setPayloadMode(v ? 'custom' : 'default')} />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Customise</span>
                      </label>
                    </div>

                    {payloadMode === 'default' ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500">A standard payload with the event, task flow, instance and step details is sent. Customise it to include the answers captured in this and earlier steps.</p>
                    ) : (
                      <div className="rounded-md border border-gray-200 dark:border-zinc-700 p-3 space-y-3">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => setBuilderTab('simple')}
                            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${builderTab === 'simple' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-zinc-700'}`}
                          >
                            <List className="h-3 w-3" /> Simple
                          </button>
                          <WithTip tip="Write the payload as raw JSON. Insert variables from the list; a live preview shows the result. For developers or advanced payloads.">
                            <button
                              type="button"
                              onClick={switchToAdvanced}
                              className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${builderTab === 'advanced' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-zinc-700'}`}
                            >
                              <Code className="h-3 w-3" /> Advanced (JSON)
                            </button>
                          </WithTip>
                        </div>

                        <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-2.5 py-1.5">
                          <p className="text-[11px] text-amber-800 dark:text-amber-200">
                            Fields you add here send the customer's answers to the destination. Only include what the receiving system needs.
                          </p>
                        </div>

                        {builderTab === 'simple' ? (
                          <div className="space-y-2">
                            {visualRows.length === 0 && (
                              <p className="text-[11px] text-gray-400 dark:text-gray-500">Add a row for each piece of data to send. Give it a key (e.g. <code>email</code> — use <code>customer.email</code> for nested JSON) and pick which value it should carry.</p>
                            )}
                            {visualRows.length > 0 && (
                              <div className="flex items-center gap-2 px-1">
                                <span className="flex-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                  Key to send
                                  <InfoTip>The name this piece of data will have in the payload the receiving system gets. Use a dot (e.g. customer.email) to nest it.</InfoTip>
                                </span>
                                <span className="flex-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                  Value to send
                                  <InfoTip>What this field carries: a captured answer, a task flow / instance / step detail, a customer detail, a step result, or a fixed value you type in.</InfoTip>
                                </span>
                                <span className="w-6" />
                              </div>
                            )}
                            {visualRows.map((r, i) => (
                              <div key={i} className="rounded-md border border-gray-200 dark:border-zinc-700 p-2 space-y-1">
                                <div className="flex items-center gap-2">
                                  <input className={`${inputClass} flex-1`} placeholder="e.g. email" value={r.key} onChange={(e) => updateRow(i, 'key', e.target.value)} />
                                  <select className={`${inputClass} flex-1`} value={r.token} onChange={(e) => updateRow(i, 'token', e.target.value)}>
                                    {stepGroups.map((g) => (
                                      <optgroup key={g.stepId} label={stepGroupLabel(g)}>
                                        {g.items.map((it) => <option key={it.key} value={it.key}>{it.label}</option>)}
                                      </optgroup>
                                    ))}
                                    {metaGroups.map((mg) => (
                                      <optgroup key={mg.group} label={mg.group}>
                                        {mg.items.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
                                      </optgroup>
                                    ))}
                                    <optgroup label="Other">
                                      <option value="__static__">Fixed value…</option>
                                    </optgroup>
                                  </select>
                                  <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-700" onClick={() => removeRow(i)}>
                                    <Trash2 className="h-3.5 w-3.5 text-gray-400" />
                                  </button>
                                </div>
                                {r.token === '__static__' && (
                                  <input className={inputClass} placeholder="Fixed value to send" value={r.staticText} onChange={(e) => updateRow(i, 'staticText', e.target.value)} />
                                )}
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 pl-0.5">
                                  Sends: <code className="text-gray-600 dark:text-gray-300">{sampleLabel(r, sampleByKey)}</code>
                                  {r.token !== '__static__' && <span> (example)</span>}
                                </p>
                              </div>
                            ))}
                            <button type="button" onClick={addRow} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
                              <Plus className="h-3 w-3" /> Add field
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="relative" ref={paletteRef}>
                                <button
                                  type="button"
                                  onClick={() => setPaletteOpen((o) => !o)}
                                  className="inline-flex items-center gap-1 rounded-md bg-white dark:bg-zinc-700 px-2 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600"
                                >
                                  <Plus className="h-3 w-3" /> Insert variable
                                  <ChevronDown className="h-3 w-3 text-gray-400" />
                                </button>
                                {paletteOpen && (
                                  <div className="absolute left-0 z-20 mt-1 w-72 rounded-md border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-lg p-2">
                                    <input
                                      autoFocus
                                      className={`${inputClass} mb-2`}
                                      placeholder="Search…"
                                      value={paletteSearch}
                                      onChange={(e) => setPaletteSearch(e.target.value)}
                                    />
                                    <div className="space-y-1.5 max-h-60 overflow-y-auto">
                                      {stepGroups.map((g) => (
                                        <PaletteGroup key={g.stepId} title={stepGroupLabel(g)} items={matchSearch(g.items)} onInsert={insertVariable} />
                                      ))}
                                      {metaGroups.map((mg) => (
                                        <PaletteGroup key={mg.group} title={mg.group} items={matchSearch(mg.items)} onInsert={insertVariable} />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="flex-1" />
                              <button type="button" onClick={formatTemplate} className="shrink-0 rounded-md bg-white dark:bg-zinc-700 px-2 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600">Format</button>
                              <button type="button" onClick={resetTemplate} className="shrink-0 rounded-md bg-white dark:bg-zinc-700 px-2 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600">Reset</button>
                            </div>

                            <JsonCodeEditor
                              ref={editorApiRef}
                              value={template}
                              onChange={setTemplate}
                            />

                            <div className="text-[11px]">
                              {template.trim() && !jsonValid ? (
                                <span className="text-red-600 dark:text-red-400">Invalid JSON — check quotes, commas and braces.</span>
                              ) : advUnknown.length ? (
                                <span className="text-red-600 dark:text-red-400">Unknown variables: {advUnknown.join(', ')}</span>
                              ) : (
                                <span className="text-green-600 dark:text-green-400">Valid ✓</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Preview — shown on demand */}
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => setShowPreview((s) => !s)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        {showPreview ? 'Hide preview' : 'Preview payload'}
                        {showPreview ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </button>
                      {showPreview && (
                        <div className="mt-2">
                          <p className="text-[11px] text-gray-400 mb-1">What will be sent, using example data.</p>
                          {previewError ? (
                            <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-2.5 py-2 text-[11px] text-red-700 dark:text-red-300">{previewError}</div>
                          ) : (
                            <pre className="rounded bg-gray-100 dark:bg-zinc-900 p-2.5 text-[11px] text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre-wrap break-words max-h-60">{preview || 'Loading…'}</pre>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Secondary settings — collapsed by default to keep the modal short */}
                  <div className="space-y-2">
                    <CollapsibleSection
                      title="Headers"
                      tip="Extra headers the receiving system needs, such as an API key or a content type. These are sent with every request. Don't put secret tokens here — use Authentication."
                      summary={headerCount ? `${headerCount} header${headerCount === 1 ? '' : 's'}` : 'None'}
                      open={!!openSections.headers}
                      onToggle={() => toggleSection('headers')}
                    >
                      <div className="space-y-2">
                        {headers.map((h, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input className={`${inputClass} flex-1`} placeholder="Header name" value={h.name} onChange={(e) => updateHeader(i, 'name', e.target.value)} />
                            <input className={`${inputClass} flex-1`} placeholder="Value" value={h.value} onChange={(e) => updateHeader(i, 'value', e.target.value)} />
                            <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-700" onClick={() => removeHeader(i)}>
                              <Trash2 className="h-3.5 w-3.5 text-gray-400" />
                            </button>
                          </div>
                        ))}
                        <button type="button" onClick={addHeader} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
                          <Plus className="h-3 w-3" /> Add header
                        </button>
                      </div>
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Authentication"
                      tip="An optional token the receiving system checks to confirm the request came from you. Choose a bearer token or a custom header. Stored securely and never shown again once saved."
                      summary={authLabel}
                      open={!!openSections.auth}
                      onToggle={() => toggleSection('auth')}
                    >
                      <select className={inputClass} value={authType} onChange={(e) => setAuthType(e.target.value)}>
                        <option value="none">None</option>
                        <option value="bearer">Bearer token</option>
                        <option value="header">Custom header</option>
                      </select>
                      {authType !== 'none' && (
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {authType === 'header' && (
                            <div>
                              <label className={labelClass}>Header name</label>
                              <input className={inputClass} value={authHeaderName} onChange={(e) => setAuthHeaderName(e.target.value)} placeholder="X-Api-Key" />
                            </div>
                          )}
                          <div className={authType === 'header' ? '' : 'sm:col-span-2'}>
                            <label className={labelClass}>{authType === 'bearer' ? 'Token' : 'Value'}</label>
                            <input
                              type="password"
                              className={inputClass}
                              value={authCredential}
                              onChange={(e) => setAuthCredential(e.target.value)}
                              placeholder={isEdit && action?.has_auth_config ? '•••••••• (leave blank to keep)' : ''}
                              autoComplete="new-password"
                            />
                          </div>
                        </div>
                      )}
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Request signing"
                      tip="Adds a signature the receiving system can verify to confirm the request genuinely came from TenantWise. When on, you're given a signing secret (shown once) to set up on your endpoint."
                      summary={signingEnabled ? 'On' : 'Off'}
                      open={!!openSections.signing}
                      onToggle={() => toggleSection('signing')}
                    >
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch checked={signingEnabled} onCheckedChange={setSigningEnabled} />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Sign requests so the receiver can verify they came from us</span>
                      </label>
                      {!signingEnabled && (
                        <div className="mt-2 rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 p-2.5">
                          <div className="flex gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-800 dark:text-amber-200">With signing off, the receiver cannot cryptographically verify the request. Requests are still sent over HTTPS. Consider adding an authentication header instead.</p>
                          </div>
                        </div>
                      )}
                      {isEdit && signingEnabled && action?.has_signing_secret && (
                        <button type="button" onClick={handleRotate} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
                          <KeyRound className="h-3 w-3" /> Rotate signing secret
                        </button>
                      )}
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Delivery settings"
                      tip="How the request is delivered. Timeout is how long to wait for a response before an attempt fails; max attempts is how many times to retry (with increasing delays) before giving up."
                      summary={`${timeoutSeconds}s timeout · ${maxAttempts} attempts`}
                      open={!!openSections.delivery}
                      onToggle={() => toggleSection('delivery')}
                    >
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelClass}>Timeout (seconds)</label>
                          <input type="number" min={1} max={30} className={inputClass} value={timeoutSeconds} onChange={(e) => setTimeoutSeconds(e.target.value)} />
                        </div>
                        <div>
                          <label className={labelClass}>Max attempts</label>
                          <input type="number" min={1} max={10} className={inputClass} value={maxAttempts} onChange={(e) => setMaxAttempts(e.target.value)} />
                        </div>
                      </div>
                    </CollapsibleSection>
                  </div>
                </div>

                <div className="flex flex-row-reverse gap-2 px-5 py-3.5 border-t border-gray-200 dark:border-zinc-700">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={handleSave}
                    className="inline-flex justify-center rounded-md bg-gray-900 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create action'}
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => setOpen(false)}
                    className="inline-flex justify-center rounded-md bg-white dark:bg-zinc-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600"
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

// Wrap any element in a hover tooltip. Content is portalled to <body> so it
// isn't clipped by the modal's scroll container, and flips/shifts to stay
// on-screen.
function WithTip({ tip, children }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipPrimitive.Portal>
          <TooltipContent className="max-w-xs z-[60]" collisionPadding={12}>
            <p className="text-xs">{tip}</p>
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  )
}

function InfoTip({ children }) {
  return (
    <WithTip tip={children}>
      <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help shrink-0" />
    </WithTip>
  )
}

function FieldLabel({ children, tip }) {
  return (
    <div className="flex items-center gap-1 mb-1">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{children}</span>
      {tip && <InfoTip>{tip}</InfoTip>}
    </div>
  )
}

// A secondary section that collapses to a single row (with a summary of its
// current setting) so the modal stays short.
function CollapsibleSection({ title, tip, summary, open, onToggle, children }) {
  return (
    <div className="rounded-md border border-gray-200 dark:border-zinc-700">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button type="button" onClick={onToggle} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {open ? <ChevronUp className="h-3.5 w-3.5 text-gray-400 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />}
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide shrink-0">{title}</span>
          {!open && summary && <span className="text-[11px] text-gray-400 truncate">{summary}</span>}
        </button>
        {tip && <InfoTip>{tip}</InfoTip>}
      </div>
      {open && <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-zinc-700">{children}</div>}
    </div>
  )
}

function SectionHeading({ children, tip }) {
  return (
    <div className="flex items-center gap-1">
      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">{children}</h4>
      {tip && <InfoTip>{tip}</InfoTip>}
    </div>
  )
}

function PaletteGroup({ title, items, onInsert }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">{title}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((it) => (
          <button
            key={it.key}
            type="button"
            title={`${it.label || it.key}${it.sample !== undefined && it.sample !== null ? ` — e.g. ${JSON.stringify(it.sample)}` : (it.description ? ` — ${it.description}` : '')}`}
            onClick={() => onInsert(it.key)}
            className="rounded bg-gray-100 dark:bg-zinc-700 px-1.5 py-0.5 text-[11px] text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-zinc-600"
          >
            {it.label || it.key}
          </button>
        ))}
      </div>
    </div>
  )
}
